import {
  Button,
  IconChevronDownSelect,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@newfront-insurance/core-ui/v2';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import type { ReactNode } from 'react';

import { AnalyticsUnavailableValue } from './analytics-unavailable-value';

export type SortDirection = 'ASC' | 'DESC';
export interface AnalyticsColumn<Row, SortField extends string> {
  key: string;
  label: string;
  sortField?: SortField;
  align?: 'left' | 'right';
  className?: string;
  render: (row: Row) => ReactNode;
}

export function AnalyticsPagination({
  label,
  testId,
  offset,
  limit,
  totalCount,
  disabled = false,
  onPageChange,
}: {
  label: string;
  testId: string;
  offset: number;
  limit: number;
  totalCount: number;
  disabled?: boolean;
  onPageChange: (offset: number) => void;
}) {
  return (
    <div data-testid={`${testId}-pagination`} className="bg-steel-50 flex items-center justify-between gap-3 px-4 py-2">
      <span
        data-testid={`${testId}-result-count`}
        className="text-steel-500 text-sm"
        aria-live={disabled ? 'off' : 'polite'}
      >
        {disabled
          ? ''
          : totalCount === 0
            ? '0 results'
            : `${offset + 1}–${Math.min(offset + limit, totalCount)} of ${totalCount}`}
      </span>
      <div className="flex" role="group" aria-label={`${label} pagination`}>
        <Button
          type="button"
          variant="secondary"
          className="h-8 w-10 rounded-r-none px-0 py-0"
          disabled={disabled || offset === 0}
          onClick={() => onPageChange(Math.max(0, offset - limit))}
          aria-label={`Previous page of ${label}`}
          data-testid={`${testId}-previous-page`}
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="-ml-px h-8 w-10 rounded-l-none px-0 py-0"
          disabled={disabled || offset + limit >= totalCount}
          onClick={() => onPageChange(offset + limit)}
          aria-label={`Next page of ${label}`}
          data-testid={`${testId}-next-page`}
        >
          <ChevronRight className="size-5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export function AnalyticsTable<Row, SortField extends string>({
  title,
  testId,
  description,
  columns,
  rows,
  rowKey,
  search,
  onSearchChange,
  toolbar,
  sortField,
  sortDirection,
  onSort,
  offset,
  limit,
  totalCount,
  onPageChange,
  loading,
  error,
  errorMessage,
  retryable = true,
  onRetry,
  emptyMessage,
}: {
  title: string;
  testId: string;
  description?: string;
  columns: AnalyticsColumn<Row, SortField>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  search: string;
  onSearchChange: (value: string) => void;
  toolbar?: ReactNode;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  offset: number;
  limit: number;
  totalCount: number;
  onPageChange: (offset: number) => void;
  loading: boolean;
  error: boolean;
  errorMessage?: string;
  retryable?: boolean;
  onRetry: () => void;
  emptyMessage: string;
}) {
  return (
    <section data-testid={testId} className="flex min-w-0 flex-col gap-3" aria-label={title}>
      <div>
        <h3 className="text-steel-500 text-lg leading-6 font-semibold">{title}</h3>
        {description ? <p className="text-steel-400 mt-1 text-sm leading-4">{description}</p> : null}
      </div>
      <div className="border-steel-200/50 overflow-hidden rounded-sm border">
        <div className="border-steel-200/50 bg-steel-50 flex flex-wrap items-center justify-between gap-3 border-b px-3.5 py-2.5">
          <Input
            testId={`${testId}-search`}
            aria-label={`Search ${title}`}
            placeholder="Search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            wrapperClassName="h-8 w-[237px] max-w-full rounded-sm border-steel-200 bg-white"
            inputClassName="py-1 text-base"
            appendIcon={<Search className="text-steel-400 size-4" aria-hidden="true" />}
          />
          {toolbar}
        </div>
        <div className="overflow-x-auto" role="region" aria-label={`${title} table`} tabIndex={0}>
          <Table
            data-testid={`${testId}-table`}
            className="w-full min-w-[700px] table-fixed text-sm"
            aria-label={title}
            aria-busy={loading}
          >
            <TableHeader>
              <TableRow className="border-steel-200/50">
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    scope="col"
                    className={cn(
                      'bg-steel-50 h-9 min-w-0 px-3.5 py-2 text-sm leading-4 font-normal',
                      column.align === 'right' && 'text-right',
                      column.className,
                    )}
                    aria-sort={
                      column.sortField === sortField
                        ? sortDirection === 'ASC'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {column.sortField ? (
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-1 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2',
                          column.align === 'right' && 'justify-end',
                        )}
                        onClick={() => {
                          if (column.sortField) onSort(column.sortField);
                        }}
                        aria-label={`Sort ${title} by ${column.label}`}
                        data-testid={`${testId}-sort-${column.key}`}
                      >
                        <span>{column.label}</span>
                        <IconChevronDownSelect
                          className={cn(
                            'size-5 shrink-0',
                            column.sortField === sortField && sortDirection === 'ASC' && 'rotate-180',
                          )}
                          aria-hidden="true"
                        />
                      </button>
                    ) : (
                      column.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="text-sm">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="p-3">
                    <div role="status" aria-label={`Loading ${title}`} className="flex flex-col gap-3">
                      {[0, 1, 2, 3].map((index) => (
                        <Skeleton key={index} className="h-6 w-full" />
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && error ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="p-6">
                    <div
                      data-testid={`${testId}-error`}
                      role="alert"
                      className="text-steel-400 flex items-center justify-center gap-3"
                    >
                      <span>{errorMessage ?? `${title} couldn’t be loaded.`}</span>
                      {retryable ? (
                        <Button
                          data-testid={`${testId}-retry`}
                          type="button"
                          variant="secondary"
                          size="small"
                          onClick={onRetry}
                        >
                          Retry {title}
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && !error && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    data-testid={`${testId}-empty`}
                    colSpan={columns.length}
                    className="text-steel-400 px-6 py-8 text-center"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && !error
                ? rows.map((row) => (
                    <TableRow
                      key={rowKey(row)}
                      data-testid={`${testId}-row`}
                      data-row-key={rowKey(row)}
                      className="border-steel-200/50 even:bg-steel-50"
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          data-testid={`${testId}-cell-${column.key}`}
                          className={cn(
                            'text-steel-500 min-w-0 px-3.5 py-2.5 leading-4 break-words',
                            column.align === 'right' && 'text-right',
                          )}
                        >
                          {column.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : null}
            </TableBody>
          </Table>
        </div>
        <AnalyticsPagination
          label={title}
          testId={testId}
          offset={offset}
          limit={limit}
          totalCount={totalCount}
          disabled={loading || error}
          onPageChange={onPageChange}
        />
      </div>
    </section>
  );
}

export function AnalyticsDate({ value, asOfDate }: { value: string | null | undefined; asOfDate: string | undefined }) {
  const formatted = formatAnalyticsDate(value, asOfDate);
  return formatted === '—' ? <AnalyticsUnavailableValue /> : formatted;
}

// Calendar-day comparison uses the report snapshot, independent of the browser's timezone.
export function formatAnalyticsDate(value: string | null | undefined, asOfDate: string | undefined): string {
  if (!value || !asOfDate) return '—';
  const date = Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  const snapshot = Date.parse(`${asOfDate.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(date) || !Number.isFinite(snapshot)) return '—';
  const days = Math.floor((snapshot - date) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1) return `${days} days ago`;
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }).format(
    date,
  );
}
